import React, { useState, useEffect } from 'react';
import { TextField, InputAdornment } from '@mui/material';

/**
 * Eine angepasste Eingabekomponente für Dezimalzahlen mit Komma- und Punkt-Unterstützung
 * 
 * @param {Object} props Alle Props, die auch an ein normales TextField übergeben werden können
 * @param {Function} onChange Die Callback-Funktion, die bei Änderungen aufgerufen wird
 * @param {string|number} value Der aktuelle Wert des Feldes
 * @param {number} min Der Minimalwert (optional)
 * @param {number} max Der Maximalwert (optional)
 * @param {number} step Die Schrittweite (optional, Standard: 0.01)
 * @param {string} endAdornment Text, der am Ende des Feldes angezeigt wird (z.B. "€" oder "%")
 * @returns {JSX.Element} Die NumberInputField-Komponente
 */
const NumberInputField = ({
  onChange,
  value,
  min,
  max,
  step = 0.01,
  endAdornment,
  ...props
}) => {
  // Interner State für die Texteingabe
  const [localValue, setLocalValue] = useState('');
  const [hasFocus, setHasFocus] = useState(false);

  // Bei Änderung des value-Prop oder beim ersten Laden
  useEffect(() => {
    // Nur aktualisieren, wenn kein Fokus besteht, um Cursor-Sprünge zu vermeiden
    if (!hasFocus && typeof value === 'number') {
      setLocalValue(value.toFixed(2).replace('.', ','));
    }
  }, [value, hasFocus]);

  // Wenn der Fokus verloren geht, formatieren wir den Wert
  const handleBlur = () => {
    setHasFocus(false);
    
    // Wert validieren und formatieren
    if (localValue === '' || localValue === '-') {
      setLocalValue('0,00');
      onChange({ target: { value: 0 } });
      return;
    }
    
    const normalizedValue = localValue.replace(',', '.');
    let parsedValue = parseFloat(normalizedValue);

    if (isNaN(parsedValue)) {
      parsedValue = 0;
    }
    
    // Begrenze den Wert auf min/max, falls angegeben
    if (min !== undefined && parsedValue < min) parsedValue = min;
    if (max !== undefined && parsedValue > max) parsedValue = max;
    
    // Aktualisiere den lokalen Wert
    setLocalValue(parsedValue.toFixed(2).replace('.', ','));
    
    // Informiere den Parent-Komponenten
    onChange({ target: { value: parsedValue } });
  };

  // Konvertiert eine deutsche Komma-Eingabe in einen Punkt für parseFloat
  const handleChange = (event) => {
    const inputValue = event.target.value;
    
    // Prüfe, ob es eine gültige Zahl ist (erlaubt Komma oder Punkt)
    // Erlaubt leere Eingabe, negative Zahlen, Dezimalzahlen
    if (inputValue === '' || inputValue === '-' || inputValue.match(/^-?\d*[.,]?\d*$/)) {
      setLocalValue(inputValue);
      
      if (inputValue !== '' && inputValue !== '-') {
        const normalizedValue = inputValue.replace(',', '.');
        let parsedValue = parseFloat(normalizedValue);
        
        if (!isNaN(parsedValue)) {
          // Begrenze den Wert auf min/max, falls angegeben
          if (min !== undefined && parsedValue < min) parsedValue = min;
          if (max !== undefined && parsedValue > max) parsedValue = max;
          
          // Informiere den Parent-Komponenten über den numerischen Wert
          onChange({ target: { value: parsedValue } });
        }
      }
    }
  };
  return (
    <TextField
      type="text"
      value={hasFocus ? localValue : (typeof value === 'number' ? value.toFixed(2).replace('.', ',') : localValue)}
      onChange={handleChange}
      onFocus={() => setHasFocus(true)}
      onBlur={handleBlur}
      InputProps={{
        endAdornment: endAdornment ? <InputAdornment position="end">{endAdornment}</InputAdornment> : null,
        inputProps: { 
          style: { textAlign: 'center' } 
        }
      }}
      {...props}
    />
  );
};

export default NumberInputField;